import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  ApiClientError,
  fetchAdminMe,
  fetchExternalMe,
  fetchMe,
  loginAdmin as apiLoginAdmin,
  loginExternal as apiLoginExternal,
  loginGuest as apiLoginGuest,
  logout as apiLogout,
  type AdminSession,
  type ExternalSession,
  type GuestSession,
} from '../lib/api';

type AuthState = {
  guest: GuestSession | null;
  admin: AdminSession | null;
  external: ExternalSession | null;
  /** True until the initial probes finish. */
  loading: boolean;
  loginGuest(name: string, password: string): Promise<void>;
  loginAdmin(username: string, password: string): Promise<void>;
  loginExternal(password: string): Promise<void>;
  logout(): Promise<void>;
};

const AuthCtx = createContext<AuthState | null>(null);

const isNot401 = (e: unknown) => !(e instanceof ApiClientError && e.status === 401);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [guest, setGuest] = useState<GuestSession | null>(null);
  const [admin, setAdmin] = useState<AdminSession | null>(null);
  const [external, setExternal] = useState<ExternalSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctl = new AbortController();
    Promise.allSettled([
      fetchMe(ctl.signal).then(setGuest),
      fetchAdminMe(ctl.signal).then(setAdmin),
      fetchExternalMe(ctl.signal).then(setExternal),
    ])
      .then((results) => {
        for (const r of results) {
          if (r.status === 'rejected' && isNot401(r.reason)) {
            console.error('auth probe failed', r.reason);
          }
        }
      })
      .finally(() => {
        if (!ctl.signal.aborted) setLoading(false);
      });
    return () => ctl.abort();
  }, []);

  const loginGuest = useCallback(async (name: string, password: string) => {
    await apiLoginGuest(name, password);
    setGuest(await fetchMe());
  }, []);

  const loginAdmin = useCallback(async (username: string, password: string) => {
    await apiLoginAdmin(username, password);
    setAdmin(await fetchAdminMe());
  }, []);

  const loginExternal = useCallback(async (password: string) => {
    await apiLoginExternal(password);
    setExternal({ kind: 'external' });
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setGuest(null);
    setAdmin(null);
    setExternal(null);
  }, []);

  return (
    <AuthCtx.Provider
      value={{ guest, admin, external, loading, loginGuest, loginAdmin, loginExternal, logout }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
